import { useEffect, useMemo, useState } from 'react';

import { useCart } from 'hooks/use-cart';
import { useCartContext } from 'state/cart';
import LocalStorage, { StorageKeys } from 'utils/local-storage';

import { useAppTakeover } from './use-app-takeover';
import type { AppTakeoverType } from './use-app-takeover';

export const useAppTakeoverCart = (): Partial<
  AppTakeoverType['cart'] & {
    isTakeoverActive: boolean;
    hasPaymentProcessingTakeover: boolean;
    fontFamily?: string;
  }
> => {
  const { cartEntries } = useCart();
  const { rbiOrderId } = useCartContext();

  const appTakeover = useAppTakeover();
  const targetMenuItemIds = appTakeover?.pdp?.targetMenuItemIds;
  const cartEntryUrls = cartEntries.map(x => x.url);
  const isAppTakeoverCart = useMemo(
    () => cartEntryUrls.some(url => targetMenuItemIds?.some(id => url.includes(id))),
    [cartEntryUrls, targetMenuItemIds]
  );

  useEffect(() => {
    if (isAppTakeoverCart && rbiOrderId) {
      LocalStorage.setItem(StorageKeys.APP_TAKEOVER_ORDER_ID, rbiOrderId);
    }
  }, [rbiOrderId, isAppTakeoverCart, appTakeover?.name]);

  if (isAppTakeoverCart) {
    return {
      ...appTakeover?.cart,
      isTakeoverActive: true,
      hasPaymentProcessingTakeover: !!appTakeover?.cart?.paymentProcessingVideo,
      fontFamily: appTakeover.fontFamily,
    };
  }

  return { isTakeoverActive: false };
};

export const useAppTakeoverOrderConfirmDetails = (
  rbiOrderId: string
): Partial<
  AppTakeoverType['orderConfirmDetails'] & {
    isTakeoverActive: boolean;
  }
> => {
  const appTakeover = useAppTakeover();
  const [isTakeoverOrder, setIsTakeoverOrder] = useState(false);

  useEffect(() => {
    const savedTakeoverId = LocalStorage.getItem(StorageKeys.APP_TAKEOVER_ORDER_ID);
    setIsTakeoverOrder(!!(rbiOrderId && savedTakeoverId && rbiOrderId === savedTakeoverId));
  }, [rbiOrderId]);

  if (!isTakeoverOrder) {
    return { isTakeoverActive: false };
  }

  return { ...appTakeover?.orderConfirmDetails, isTakeoverActive: true };
};
